/**
 * Direct Supabase reads for the reviewer dashboard. Unlike the valuer upload flow (which
 * goes through the ingestion server, ../api.ts), the reviewer reads straight from the
 * tables: RLS's `is_reviewer()` policy (Phase 0 migration) already grants full SELECT
 * access to every table the dashboard needs, and Realtime is enabled on
 * reports/check_results/risk_results/error_findings for that same role. Writes (status,
 * notes) and signed URLs still go through the worker since those need the service role.
 */
import { supabase } from '../supabase';
import type {
  AnnotationRow,
  AnnotationType,
  BankRow,
  CheckResultRow,
  CheckSchemaRow,
  CheckStatus,
  DocumentRow,
  ErrorFindingRow,
  ErrorType,
  FeedbackDispatchRow,
  ReportRow,
  ReportStatus,
  ReportSummary,
  RiskResultRow,
  RiskSchemaRow,
  RiskStatus,
  Severity,
  ValuerRow,
} from './types';

interface RawValuer {
  id: string;
  display_name: string;
  email: string;
}
export async function fetchValuers(): Promise<ValuerRow[]> {
  const { data, error } = await supabase.from('valuers').select('id, display_name, email').order('display_name');
  if (error) throw error;
  return ((data ?? []) as RawValuer[]).map((v) => ({ id: v.id, displayName: v.display_name, email: v.email }));
}

interface RawBank {
  id: string;
  name: string;
}
export async function fetchBanks(): Promise<BankRow[]> {
  const { data, error } = await supabase.from('banks').select('id, name');
  if (error) throw error;
  return (data ?? []) as RawBank[];
}

export interface RawReport {
  id: string;
  address_raw: string | null;
  address_normalised: string | null;
  valuer_id: string | null;
  orderer_bank_id: string | null;
  status: ReportStatus;
  submitted_at: string | null;
  first_seen_at: string;
  checked_at: string | null;
  reviewer_notes: string | null;
  valuer_notes: string | null;
  created_at: string;
  needed_by_at: string | null;
  revision_number: number;
  revision_of_report_id: string | null;
  report_summary: Record<string, unknown> | null;
}

/** Defensive mapping of the report_summary jsonb (written by the worker's ReportSummarySchema) - a malformed payload becomes null, never a crash. */
function mapReportSummary(raw: Record<string, unknown> | null | undefined): ReportSummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const str = (v: unknown) => (typeof v === 'string' && v.trim() !== '' ? v : null);
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  return {
    reportType: str(raw.report_type),
    propertyDescription: str(raw.property_description),
    dwellingCount: num(raw.dwelling_count),
    dwellingType: str(raw.dwelling_type),
    landArea: str(raw.land_area),
    buildingArea: str(raw.building_area),
    averageDwellingSize: str(raw.average_dwelling_size),
    valueRateRange: str(raw.value_rate_range),
    priceRange: str(raw.price_range),
    zoning: str(raw.zoning),
    keyFacts: Array.isArray(raw.key_facts) ? raw.key_facts.filter((f): f is string => typeof f === 'string' && f.trim() !== '') : [],
  };
}

export function mapReport(r: RawReport): ReportRow {
  return {
    id: r.id,
    addressRaw: r.address_raw,
    addressNormalised: r.address_normalised,
    valuerId: r.valuer_id,
    ordererBankId: r.orderer_bank_id,
    status: r.status,
    submittedAt: r.submitted_at,
    firstSeenAt: r.first_seen_at,
    checkedAt: r.checked_at,
    reviewerNotes: r.reviewer_notes,
    valuerNotes: r.valuer_notes,
    createdAt: r.created_at,
    neededByAt: r.needed_by_at,
    revisionNumber: r.revision_number,
    revisionOfReportId: r.revision_of_report_id,
    reportSummary: mapReportSummary(r.report_summary),
  };
}
const REPORT_COLUMNS =
  'id, address_raw, address_normalised, valuer_id, orderer_bank_id, status, submitted_at, first_seen_at, checked_at, reviewer_notes, valuer_notes, created_at, needed_by_at, revision_number, revision_of_report_id, report_summary';

export async function fetchReports(): Promise<ReportRow[]> {
  const { data, error } = await supabase.from('reports').select(REPORT_COLUMNS).order('created_at', { ascending: false });
  if (error) throw error;
  return ((data ?? []) as RawReport[]).map(mapReport);
}

export async function fetchReport(reportId: string): Promise<ReportRow | null> {
  const { data, error } = await supabase.from('reports').select(REPORT_COLUMNS).eq('id', reportId).maybeSingle();
  if (error) throw error;
  return data ? mapReport(data as RawReport) : null;
}

interface RawDocument {
  id: string;
  report_id: string;
  kind: 'report' | 'letter_of_instruction' | 'other';
  page_count: number | null;
}
export async function fetchDocuments(reportId: string): Promise<DocumentRow[]> {
  const { data, error } = await supabase
    .from('documents')
    .select('id, report_id, kind, page_count')
    .eq('report_id', reportId);
  if (error) throw error;
  return ((data ?? []) as RawDocument[]).map((d) => ({
    id: d.id,
    reportId: d.report_id,
    kind: d.kind,
    pageCount: d.page_count,
  }));
}

interface RawCheckSchema {
  check_key: string;
  category: string;
  label: string;
  description: string | null;
  display_order: number;
}
export async function fetchCheckSchema(): Promise<CheckSchemaRow[]> {
  const { data, error } = await supabase
    .from('check_schema')
    .select('check_key, category, label, description, display_order')
    .eq('active', true)
    .order('display_order');
  if (error) throw error;
  return ((data ?? []) as RawCheckSchema[]).map((c) => ({
    checkKey: c.check_key,
    category: c.category,
    label: c.label,
    description: c.description,
    displayOrder: c.display_order,
  }));
}

interface RawRiskSchema {
  risk_key: string;
  risk_group: 'property' | 'market';
  label: string;
  description: string | null;
  display_order: number;
  comment_required_above: number;
}
export async function fetchRiskSchema(): Promise<RiskSchemaRow[]> {
  const { data, error } = await supabase
    .from('risk_matrix_schema')
    .select('risk_key, risk_group, label, description, display_order, comment_required_above')
    .eq('active', true)
    .order('display_order');
  if (error) throw error;
  return ((data ?? []) as RawRiskSchema[]).map((r) => ({
    riskKey: r.risk_key,
    riskGroup: r.risk_group,
    label: r.label,
    description: r.description,
    displayOrder: r.display_order,
    commentRequiredAbove: r.comment_required_above,
  }));
}

export interface RawCheckResult {
  id: string;
  report_id: string;
  check_key: string;
  status: CheckStatus;
  reason: string | null;
  page_ref: number | null;
  evidence_snippet: string | null;
  confidence: number | null;
}
export function mapCheckResult(c: RawCheckResult): CheckResultRow {
  return {
    id: c.id,
    reportId: c.report_id,
    checkKey: c.check_key,
    status: c.status,
    reason: c.reason,
    pageRef: c.page_ref,
    evidenceSnippet: c.evidence_snippet,
    confidence: c.confidence,
  };
}
const CHECK_RESULT_COLUMNS = 'id, report_id, check_key, status, reason, page_ref, evidence_snippet, confidence';

export async function fetchAllCheckResults(): Promise<CheckResultRow[]> {
  const { data, error } = await supabase.from('check_results').select(CHECK_RESULT_COLUMNS);
  if (error) throw error;
  return ((data ?? []) as RawCheckResult[]).map(mapCheckResult);
}

export async function fetchCheckResultsForReport(reportId: string): Promise<CheckResultRow[]> {
  const { data, error } = await supabase.from('check_results').select(CHECK_RESULT_COLUMNS).eq('report_id', reportId);
  if (error) throw error;
  return ((data ?? []) as RawCheckResult[]).map(mapCheckResult);
}

export interface RawRiskResult {
  id: string;
  report_id: string;
  risk_key: string;
  score: number | null;
  comment_present: boolean;
  comment_text: string | null;
  page_ref: number | null;
  status: RiskStatus;
  reason: string | null;
  confidence: number | null;
}
export function mapRiskResult(r: RawRiskResult): RiskResultRow {
  return {
    id: r.id,
    reportId: r.report_id,
    riskKey: r.risk_key,
    score: r.score,
    commentPresent: r.comment_present,
    commentText: r.comment_text,
    pageRef: r.page_ref,
    status: r.status,
    reason: r.reason,
    confidence: r.confidence,
  };
}
const RISK_RESULT_COLUMNS = 'id, report_id, risk_key, score, comment_present, comment_text, page_ref, status, reason, confidence';

export async function fetchAllRiskResults(): Promise<RiskResultRow[]> {
  const { data, error } = await supabase.from('risk_results').select(RISK_RESULT_COLUMNS);
  if (error) throw error;
  return ((data ?? []) as RawRiskResult[]).map(mapRiskResult);
}

export async function fetchRiskResultsForReport(reportId: string): Promise<RiskResultRow[]> {
  const { data, error } = await supabase.from('risk_results').select(RISK_RESULT_COLUMNS).eq('report_id', reportId);
  if (error) throw error;
  return ((data ?? []) as RawRiskResult[]).map(mapRiskResult);
}

export interface RawErrorFinding {
  id: string;
  report_id: string;
  error_type: ErrorType;
  description: string | null;
  page_ref: number | null;
  severity: Severity | null;
  evidence_snippet: string | null;
  suggested_fix: string | null;
  confidence: number | null;
}
export function mapErrorFinding(e: RawErrorFinding): ErrorFindingRow {
  return {
    id: e.id,
    reportId: e.report_id,
    errorType: e.error_type,
    description: e.description,
    pageRef: e.page_ref,
    severity: e.severity,
    evidenceSnippet: e.evidence_snippet,
    suggestedFix: e.suggested_fix,
    confidence: e.confidence,
  };
}
const ERROR_FINDING_COLUMNS =
  'id, report_id, error_type, description, page_ref, severity, evidence_snippet, suggested_fix, confidence';

export async function fetchAllErrorFindings(): Promise<ErrorFindingRow[]> {
  const { data, error } = await supabase.from('error_findings').select(ERROR_FINDING_COLUMNS);
  if (error) throw error;
  return ((data ?? []) as RawErrorFinding[]).map(mapErrorFinding);
}

export async function fetchErrorFindingsForReport(reportId: string): Promise<ErrorFindingRow[]> {
  const { data, error } = await supabase.from('error_findings').select(ERROR_FINDING_COLUMNS).eq('report_id', reportId);
  if (error) throw error;
  return ((data ?? []) as RawErrorFinding[]).map(mapErrorFinding);
}

// --- Markup + feedback (Phase 7) ------------------------------------------------------
// Same pattern as the rest of this module: RLS already grants the reviewer/admin SELECT on
// report_annotations/feedback_dispatches (see the Phase 7 migration), so reads go straight
// to Supabase. Writes (create/update/delete/send) go through the worker - see ../api.ts.

interface RawAnnotation {
  id: string;
  report_id: string;
  page: number;
  type: AnnotationType;
  coordinates: unknown;
  color: string;
  text: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}
export function mapAnnotation(a: RawAnnotation): AnnotationRow {
  return {
    id: a.id,
    reportId: a.report_id,
    page: a.page,
    type: a.type,
    coordinates: a.coordinates as AnnotationRow['coordinates'],
    color: a.color,
    text: a.text,
    createdBy: a.created_by,
    createdAt: a.created_at,
    updatedAt: a.updated_at,
  };
}
const ANNOTATION_COLUMNS = 'id, report_id, page, type, coordinates, color, text, created_by, created_at, updated_at';

export async function fetchAnnotationsForReport(reportId: string): Promise<AnnotationRow[]> {
  const { data, error } = await supabase.from('report_annotations').select(ANNOTATION_COLUMNS).eq('report_id', reportId);
  if (error) throw error;
  return ((data ?? []) as RawAnnotation[]).map(mapAnnotation);
}

interface RawDispatch {
  id: string;
  report_id: string;
  dispatched_at: string;
  dispatched_by: string;
  annotated_copy_storage_path: string;
  resend_message_id: string | null;
  valuer_viewed_at: string | null;
}
function mapDispatch(d: RawDispatch): FeedbackDispatchRow {
  return {
    id: d.id,
    reportId: d.report_id,
    dispatchedAt: d.dispatched_at,
    dispatchedBy: d.dispatched_by,
    annotatedCopyStoragePath: d.annotated_copy_storage_path,
    resendMessageId: d.resend_message_id,
    valuerViewedAt: d.valuer_viewed_at,
  };
}
const DISPATCH_COLUMNS = 'id, report_id, dispatched_at, dispatched_by, annotated_copy_storage_path, resend_message_id, valuer_viewed_at';

export async function fetchDispatchesForReport(reportId: string): Promise<FeedbackDispatchRow[]> {
  const { data, error } = await supabase
    .from('feedback_dispatches')
    .select(DISPATCH_COLUMNS)
    .eq('report_id', reportId)
    .order('dispatched_at', { ascending: false });
  if (error) throw error;
  return ((data ?? []) as RawDispatch[]).map(mapDispatch);
}

/** Live updates to a report's markup while the workspace is open (e.g. a second admin session editing the same report). */
export function subscribeReportAnnotations(reportId: string, onChange: () => void): () => void {
  const channel = supabase
    .channel(`report-annotations-${reportId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'report_annotations', filter: `report_id=eq.${reportId}` },
      onChange,
    )
    .subscribe();
  return () => {
    void supabase.removeChannel(channel);
  };
}

/**
 * The full revision chain for a report - every round of upload/resubmission, oldest first.
 * Walks revision_of_report_id backward to the original, then forward to find any newer
 * resubmissions, so the reviewer can open and compare any round regardless of which one
 * they started from.
 */
export async function fetchRevisionChain(reportId: string): Promise<ReportRow[]> {
  const anchor = await fetchReport(reportId);
  if (!anchor) return [];

  const chain = new Map<string, ReportRow>([[anchor.id, anchor]]);

  let current = anchor;
  while (current.revisionOfReportId) {
    const parent = await fetchReport(current.revisionOfReportId);
    if (!parent || chain.has(parent.id)) break;
    chain.set(parent.id, parent);
    current = parent;
  }

  let frontier = [anchor];
  while (frontier.length > 0) {
    const { data, error } = await supabase
      .from('reports')
      .select(REPORT_COLUMNS)
      .in(
        'revision_of_report_id',
        frontier.map((r) => r.id),
      );
    if (error) throw error;
    const children = ((data ?? []) as RawReport[]).map(mapReport).filter((r) => !chain.has(r.id));
    for (const child of children) chain.set(child.id, child);
    frontier = children;
  }

  return Array.from(chain.values()).sort((a, b) => a.revisionNumber - b.revisionNumber);
}

// --- Cost ledger (Phase 8, Part V) ---------------------------------------------------
// The reviewer/admin read policy on report_cost_ledger makes per-report AI spend
// directly queryable - cost is never invisible.

export interface ReportCostSummary {
  totalUsd: number;
  callCount: number;
}

export async function fetchReportCost(reportId: string): Promise<ReportCostSummary> {
  const { data, error } = await supabase.from('report_cost_ledger').select('cost_usd').eq('report_id', reportId);
  if (error) throw error;
  const rows = (data ?? []) as Array<{ cost_usd: number | string }>;
  return {
    totalUsd: rows.reduce((sum, r) => sum + (Number(r.cost_usd) || 0), 0),
    callCount: rows.length,
  };
}

export type RealtimeEventType = 'INSERT' | 'UPDATE' | 'DELETE';
export type RealtimeTable = 'reports' | 'check_results' | 'risk_results' | 'error_findings';

/**
 * Subscribe to live changes on the four tables the dashboard shows. RLS still applies to
 * the subscriber, so only rows the reviewer can already SELECT are ever delivered.
 * Returns an unsubscribe function.
 */
export function subscribeReviewerRealtime(
  onChange: (table: RealtimeTable, eventType: RealtimeEventType, row: Record<string, unknown>, oldRow: Record<string, unknown>) => void,
): () => void {
  const channel = supabase
    .channel('reviewer-dashboard')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'reports' },
      (payload) => onChange('reports', payload.eventType as RealtimeEventType, payload.new, payload.old),
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'check_results' },
      (payload) => onChange('check_results', payload.eventType as RealtimeEventType, payload.new, payload.old),
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'risk_results' },
      (payload) => onChange('risk_results', payload.eventType as RealtimeEventType, payload.new, payload.old),
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'error_findings' },
      (payload) => onChange('error_findings', payload.eventType as RealtimeEventType, payload.new, payload.old),
    )
    .subscribe();

  return () => {
    void supabase.removeChannel(channel);
  };
}
